import { useTranslation } from 'react-i18next'

import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES } from '@/shared/i18n/config'
import { useLocale } from '@/shared/i18n/useLocale'
import { cn } from '@/shared/ui/cn'

/**
 * Switching language must preserve the route and application state (NFR-I3),
 * so this changes the i18next language and nothing else — no navigation, no
 * reload. `DirectionProvider` picks the change up and flips `<html dir>`.
 *
 * **One plain text button naming the *other* language, in that language** —
 * "العربية" on an English page, "English" on an Arabic one — as the brand
 * reference's header has it (Task 4, 1 October 2026; it replaced a two-segment
 * control). With two languages, the only useful thing the control can say is
 * where it goes.
 *
 * ⚠️ **The accessible name is the visible word, deliberately not "Switch to
 * Arabic".** The reference used `aria-label="Switch to Arabic"`, which fails
 * WCAG 2.5.3 (label in name): a voice-control user says what they see —
 * "click العربية" — and a name that does not contain it matches nothing. So
 * the name is the language's own name, marked with its own `lang` so a screen
 * reader pronounces it in that language, and what the button *does* is its
 * description (`title`, "Switch the interface language"), read after the name.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation('common')
  const { language, setLanguage } = useLocale()

  // Two languages, so "the other one" is the first that is not current.
  const other = SUPPORTED_LANGUAGES.find((code) => code !== language) ?? SUPPORTED_LANGUAGES[0]

  return (
    <button
      type="button"
      title={t('nav.switchLanguage')}
      onClick={() => void setLanguage(other)}
      className={cn(
        // The reference's `.header-actions button`: no border, no fill, the
        // nav's 14px. A hover wash rather than a colour change alone, so the
        // pointer has something to land on.
        'rounded-[5px] px-2 py-1.5 text-sm text-fg transition-colors hover:bg-hover hover:text-brand-fg',
        className,
      )}
    >
      {/* `lang` on the word, not the button: the button's `title` is in the
          page's language, and only the name is in the other one. The Arabic
          word takes the Arabic face explicitly: on an English page `<html>`
          is not `lang="ar"`, so the page-level face rule does not reach it. */}
      <span lang={other} className={other === 'ar' ? 'font-arabic' : undefined}>
        {LANGUAGE_LABELS[other]}
      </span>
    </button>
  )
}
